import { Controller } from "@hotwired/stimulus"

// Generic tab switcher for Cup::Tabs::Component. Toggles the visible panel and
// the aria-selected state on the tab buttons. First tab is active on connect;
// switching is purely client-side (no server round-trip), so it works inside a
// turbo frame.
//
// With a `restoreKey` value set, the active tab is remembered in sessionStorage
// under that key and restored on connect. This survives the controller
// reconnecting after a turbo-frame reload — otherwise the freshly rendered
// markup (first tab `aria-selected`) would snap the user back to tab 0.
export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = { restoreKey: String }

  connect() {
    const stored = this.#storedIndex()
    if (stored !== null) {
      this.show(stored)
      return
    }
    const active = this.tabTargets.findIndex(
      (tab) => tab.getAttribute("aria-selected") === "true"
    )
    this.show(active < 0 ? 0 : active)
  }

  select(event) {
    this.show(Number(event.params.index))
  }

  show(index) {
    this.tabTargets.forEach((tab, i) => {
      tab.setAttribute("aria-selected", i === index ? "true" : "false")
    })
    this.panelTargets.forEach((panel, i) => {
      panel.classList.toggle("hidden", i !== index)
    })
    this.#storeIndex(index)
  }

  #storageKey() {
    return this.restoreKeyValue ? `tabs:${this.restoreKeyValue}` : null
  }

  #storedIndex() {
    const key = this.#storageKey()
    if (!key) return null
    const raw = window.sessionStorage.getItem(key)
    if (raw === null) return null
    const index = Number(raw)
    return index >= 0 && index < this.tabTargets.length ? index : null
  }

  #storeIndex(index) {
    const key = this.#storageKey()
    if (key) window.sessionStorage.setItem(key, String(index))
  }
}
